import type { BodyOf } from "../../../../../core/api";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";

export type PatientContact = NonNullable<PatientProfilePatient["contacts"]>[number];
export type EditPatientContact = BodyOf<
  "post",
  "./patients/:patientId/contacts"
>["contacts"][number];

export const relationshipOptions = [
  "Father",
  "Mother",
  "Son",
  "Daughter",
  "Brother",
  "Sister",
  "Wife",
  "Husband",
  "Grandchild",
  "Mother-in-law",
  "Father-in-law",
  "Legal guardian",
  "Caregiver",
  "Case Manager",
  "Plan",
  "Physician",
  "Social Worker",
  "Other",
];

function normalizePhone(value: string | null | undefined) {
  if (value === null || value === undefined || value.trim() === "") {
    return "";
  }

  if (phoneFormatter.isValidNumber(value)) {
    return phoneFormatter.formatE164(value);
  }

  return value;
}

export function createEmptyContact(): EditPatientContact {
  return {
    firstName: "",
    middleName: null,
    lastName: "",
    relationship: "",
    mobilePhoneNumber: "",
    homePhoneNumber: null,
    email: null,
    address: null,
    addressComponents: null,
    address2: null,
    isEmergencyContact: false,
    livesWithPatient: false,
  };
}

export function mapContactToEditContact(contact: PatientContact): EditPatientContact {
  return {
    id: contact.id,
    firstName: contact.firstName ?? "",
    middleName: contact.middleName ?? null,
    lastName: contact.lastName ?? "",
    relationship: contact.relationship ?? "",
    mobilePhoneNumber: normalizePhone(contact.mobilePhoneNumber),
    homePhoneNumber: contact.homePhoneNumber ? normalizePhone(contact.homePhoneNumber) : null,
    email: contact.email ?? null,
    address: contact.address ?? null,
    addressComponents: contact.addressComponents ?? null,
    address2: contact.address2 ?? null,
    isEmergencyContact: false,
    livesWithPatient: contact.livesWithPatient ?? false,
  };
}

export function normalizeContact(contact: EditPatientContact): EditPatientContact {
  const homePhoneNumber = contact.homePhoneNumber ?? "";
  const mobilePhoneNumber = contact.mobilePhoneNumber ?? "";

  return {
    ...contact,
    address: contact.address?.trim() || null,
    address2: contact.address2?.trim() || null,
    email: contact.email?.trim() || null,
    firstName: contact.firstName.trim(),
    homePhoneNumber:
      homePhoneNumber.trim() === "" ? null : phoneFormatter.formatE164(homePhoneNumber),
    lastName: contact.lastName.trim(),
    middleName: contact.middleName?.trim() || null,
    mobilePhoneNumber:
      mobilePhoneNumber.trim() === "" ? "" : phoneFormatter.formatE164(mobilePhoneNumber),
    relationship: contact.relationship.trim(),
  };
}

export function getContactsValidationMessage(contacts: EditPatientContact[]) {
  const invalidContactIndex = contacts.findIndex((contact) => {
    const homePhoneNumber = contact.homePhoneNumber ?? "";
    const mobilePhoneNumber = contact.mobilePhoneNumber ?? "";

    return (
      contact.firstName.trim() === "" ||
      contact.lastName.trim() === "" ||
      contact.relationship.trim() === "" ||
      (mobilePhoneNumber.trim() !== "" && !phoneFormatter.isValidNumber(mobilePhoneNumber)) ||
      (homePhoneNumber.trim() !== "" && !phoneFormatter.isValidNumber(homePhoneNumber))
    );
  });

  if (invalidContactIndex === -1) {
    return null;
  }

  return `Contact #${invalidContactIndex + 1} has missing or invalid required fields.`;
}
